import {
    useEffect,
    useRef,
    useState,
} from "react";

import {
    useNavigate,
    useSearchParams,
} from "react-router-dom";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import Lottie from "lottie-react";

import {
    exchangeSessionCode,
} from "../../api/journey.api";

import {
    setJourney,
    clearJourney,
} from "../../store/journeySlice";

import {
    clearAuth,
} from "../../store/authSlice";

import type {
    RootState,
    AppDispatch,
} from "../../store";

import loaderAnimation
    from "../../assets/Cleverpe_Loading.json";

import { ApiError } from "../../api/ApiError";


const AuthEntry = () => {

    const navigate =
        useNavigate();

    const dispatch =
        useDispatch<AppDispatch>();

    const [searchParams] =
        useSearchParams();

    const [error, setError] =
        useState<string | null>(null);

    const initializedRef =
        useRef(false);


    const existingJourney =
        useSelector(
            (state: RootState) =>
                state.journey.journey
        );

    const isAuthenticated =
        useSelector(
            (state: RootState) =>
                state.auth.isAuthenticated
        );


    useEffect(() => {

        if (initializedRef.current) {
            return;
        }

        initializedRef.current = true;


        const initializeJourney =
            async () => {

                const code =
                    searchParams.get("code");


                if (!code) {

                    setError(
                        "Invalid journey link."
                    );

                    return;
                }


                try {

                    /*
                     * =========================================
                     * 1. Exchange URL code
                     * =========================================
                     */

                    const response =
                        await exchangeSessionCode(code);


                    if (
                        !response.success ||
                        !response.data
                    ) {

                        throw new Error(
                            "Unable to initialize journey."
                        );

                    }


                    const newJourney =
                        response.data;


                    /*
                     * =========================================
                     * 2. Check whether this is a NEW journey
                     * =========================================
                     */

                    const isDifferentJourney =
                        !existingJourney ||
                        existingJourney.journeyId !==
                        newJourney.journeyId;


                    /*
                     * =========================================
                     * 3. NEW LINK / NEW JOURNEY
                     * =========================================
                     *
                     * Clear previous authenticated session.
                     */

                    if (isDifferentJourney) {

                        dispatch(
                            clearAuth()
                        );

                        dispatch(
                            clearJourney()
                        );

                    }


                    /*
                     * =========================================
                     * 4. Store the journey belonging to
                     *    THIS URL code
                     * =========================================
                     */

                    dispatch(
                        setJourney(
                            newJourney
                        )
                    );


                    /*
                     * =========================================
                     * 5. NEW JOURNEY
                     *
                     * User must authenticate.
                     * =========================================
                     */

                    if (isDifferentJourney) {

                        navigate(
                            "/Nlogin",
                            {
                                replace: true,
                            }
                        );

                        return;
                    }


                    /*
                     * =========================================
                     * 6. SAME JOURNEY
                     *
                     * If already authenticated, don't ask
                     * for OTP again.
                     * =========================================
                     */

                    if (isAuthenticated) {

                        switch (
                        newJourney.phase
                        ) {

                            case "ADDRESS":

                                navigate(
                                    "/naddress",
                                    {
                                        replace: true,
                                    }
                                );

                                break;


                            case "PRE_APPROVAL":

                                navigate(
                                    "/npre-approval",
                                    {
                                        replace: true,
                                    }
                                );

                                break;


                            case "SCHEME_SELECTION":

                                navigate(
                                    "/nscheme-selection",
                                    {
                                        replace: true,
                                    }
                                );

                                break;


                            default:

                                navigate(
                                    "/ndashboard",
                                    {
                                        replace: true,
                                    }
                                );

                                break;

                        }

                        return;
                    }


                    /*
                     * =========================================
                     * 7. SAME JOURNEY BUT NOT AUTHENTICATED
                     * =========================================
                     */

                    navigate(
                        "/Nlogin",
                        {
                            replace: true,
                        }
                    );

                } catch (error) {

                    let message =
                        "Unable to initialize journey.";

                    if (error instanceof ApiError) {

                        if (
                            error.status >= 500 &&
                            error.status < 600
                        ) {
                            message =
                                "Something went wrong. Please try again later.";
                        } else {
                            message =
                                error.message?.trim() ||
                                message;
                        }
                    }

                    setError(message);

                }

            };


        initializeJourney();

    }, [
        dispatch,
        navigate,
        searchParams,
        existingJourney,
        isAuthenticated,
    ]);


    if (error) {

        return (
            <div className="flex items-center justify-center min-h-screen bg-white">

                <div className="text-center px-6">

                    <h2 className="text-lg font-semibold text-gray-800">
                        Unable to continue
                    </h2>

                    <p className="mt-2 text-sm text-gray-500">
                        {error}
                    </p>

                </div>

            </div>
        );

    }


    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-white">

            <div className="w-40 h-40">

                <Lottie
                    animationData={loaderAnimation}
                    loop
                />

            </div>

            <p className="mt-4 text-sm font-semibold text-indigo-600">
                Preparing your application...
            </p>

        </div>
    );

};


export default AuthEntry;